import React, { useEffect, useRef, useState } from "react";
import styles from "./Header.module.scss"
import {SVGAdiona} from "../assets/PackageSVG";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useLocation } from "react-router-dom";
import { Check, Globe, Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { LocationPicker } from "./LocationPicker";
import { AccountAvatar, AccountMenu } from "./AccountMenu";

type HeaderProps = {
    compact?: boolean;
};

const NAV_LINKS = [
    { to: '/about', label: 'About' },
    { to: '/business', label: 'For Business' },
    { to: '/chat', label: 'Chat' },
];

/* The account menu manages itself (AccountMenu); this covers the rest. */
type DropdownId = 'locale';

export const Header = ({ compact = false }: HeaderProps)=> {
    const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    /* The links collapse into this menu below 860px, where the row no
       longer fits beside the wordmark and the account controls. */
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdown, setDropdown] = useState<DropdownId | null>(null);
    const localeRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setMenuOpen(false);
        setDropdown(null);
    }, [location.pathname]);
    useEffect(() => {
        if (!menuOpen && !dropdown) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setMenuOpen(false);
            setDropdown(null);
        };
        const onMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (localeRef.current?.contains(target)) return;
            setDropdown(null);
        };
        window.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onMouseDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onMouseDown);
        };
    }, [menuOpen, dropdown]);

    const toggleDropdown = (id: DropdownId) => setDropdown((current) => (current === id ? null : id));
    const pillClass = `${styles.pill} ${compact ? styles.pillCompact : ''}`;

    /* The header floats transparently over the hero photo, which leaves it
       unreadable the moment the page scrolls and cards pass underneath it.
       Past the first screenful it takes on a background of its own. */
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <nav className={`${styles.Header} ${compact ? styles.HeaderCompact : ''} ${scrolled ? styles.HeaderScrolled : ''}`}>
            <div className={`${styles.navDiv} ${compact ? styles.navDivCompact : ''}`}>
                {/* The links used to sit inside this Link — nested anchors,
                    which the browser unnests, so they navigated to "/". */}
                <div className={`${styles.navLeft} ${compact ? styles.navLeftCompact : ''}`}>
                    <Link to={"/"} className={`${styles.logoContainer} ${compact ? styles.logoContainerCompact : ''}`}>
                        <SVGAdiona/>
                        {/*<div className={styles.logoBox}>*/}
                        {/*    <span className={styles.logoLetter}>M</span>*/}
                        {/*</div>*/}
                        <span className={`${styles.logoText} ${compact ? styles.logoTextCompact : ''}`}>Adiona</span>
                    </Link>

                    <div className={`${styles.linksRow} ${compact ? styles.linksRowCompact : ''}`}>
                        {NAV_LINKS.map(({ to, label }) => (
                            <Link
                                key={to}
                                to={to}
                                className={`${styles.navLink} ${compact ? styles.navLinkCompact : ''}`}
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>

                <div className={`${styles.logInContainer} ${compact ? styles.logInContainerCompact : ''}`}>
                    {/* Signed in, the theme switch lives in the account menu. */}
                    {!isAuthenticated && (
                        <button
                            className={`${pillClass} ${styles.iconPill}`}
                            onClick={toggleTheme}
                            type="button"
                            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                        >
                            {theme === 'dark' ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
                        </button>
                    )}

                    <LocationPicker
                        triggerClassName={`${pillClass} ${styles.locationPill}`}
                        labelClassName={styles.locationLabel}
                    />

                    {/* The app is English-only and prices come back in US
                        dollars; this shows those settings rather than
                        offering a switch that wouldn't change anything. */}
                    <div className={`${styles.dropdownAnchor} ${styles.localeAnchor}`} ref={localeRef}>
                        <button
                            className={pillClass}
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded={dropdown === 'locale'}
                            onClick={() => toggleDropdown('locale')}
                        >
                            <Globe size={16} aria-hidden="true" />
                            EN · USD
                        </button>
                        {dropdown === 'locale' && (
                            <div className={styles.dropdown} role="dialog" aria-label="Language and currency">
                                <p className={styles.dropdownLabel}>Language</p>
                                <div className={styles.dropdownRow}>English <Check size={15} aria-hidden="true" /></div>
                                <p className={styles.dropdownLabel}>Currency</p>
                                <div className={styles.dropdownRow}>US dollar · USD <Check size={15} aria-hidden="true" /></div>
                                <p className={styles.dropdownNote}>More languages and currencies are on the way.</p>
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <span className={styles.avatarPlaceholder} aria-hidden="true" />
                    ) : isAuthenticated ? (
                        <AccountMenu triggerClassName={styles.avatarButton}>
                            <AccountAvatar />
                        </AccountMenu>
                    ) : (
                        <button className={pillClass} type="button" onClick={() => loginWithRedirect()}>
                            Sign in
                        </button>
                    )}
                    <button
                        className={`${pillClass} ${styles.iconPill} ${styles.menuBtn}`}
                        onClick={() => setMenuOpen(prev => !prev)}
                        type="button"
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}
                    >
                        {menuOpen
                            ? <X size={18} aria-hidden="true" />
                            : <Menu size={18} aria-hidden="true" />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div className={styles.mobileMenu}>
                    {NAV_LINKS.map(({ to, label }) => (
                        <Link
                            key={to}
                            to={to}
                            className={styles.mobileMenuLink}
                            onClick={() => setMenuOpen(false)}
                        >
                            {label}
                        </Link>
                    ))}
                </div>
            )}
        </nav>
    )
}